<template>
  <div
    v-if="hasTierOneSubNavItems"
    class="absolute top-0 left-full nav__tier-1 w-full lg:top-full lg:left-0"
    qaid="nav-tier-one-tab-layout"
    role="none"
  >
    <ul
      class="list-none p-0 m-0 -mb-px w-full flex flex-col lg:flex-row lg:justify-center overflow-y-auto overflow-x-hidden overscroll-contain"
      style="max-height: calc(100dvh - 60px)"
      role="menu"
    >
      <li v-if="isCompactView" class="sticky top-0" role="none">
        <nav-back-button :label="label" />
      </li>
      <li v-for="navItem in filteredNavItems" :key="navItem.uid" role="none">
        <a
          v-if="navItemVisible(navItem)"
          role="menuitem"
          class="px-3 py-2 nav-item__tier-1 nav__menu-item font-bold text-sm lg:text-xs flex justify-between items-center"
          aria-expanded="false"
          href="#"
          tabindex="-1"
          :qaid="`nav-tab-${navItem.uid}`"
          @click.prevent="focusNewTier"
          @keydown.prevent.exact.enter="focusNewTier"
          @keydown.prevent.down="
            (e) => {
              !isCompactView ? focusNewTier(e) : focusNextMenuItem(e);
            }
          "
          @keydown.prevent.up="
            (e) => {
              !isCompactView ? focusParentTier(e) : focusPreviousMenuItem(e);
            }
          "
          @keydown.prevent.shift.enter="focusParentTier"
          @keydown.prevent.right="
            (e) => {
              !isCompactView ? focusNextMenuItem(e) : focusNewTier(e);
            }
          "
          @keydown.prevent.left="
            (e) => {
              !isCompactView ? focusPreviousMenuItem(e) : focusParentTier(e);
            }
          "
        >
          <span class="pointer-events-none" v-html="navItem.url.linkName" />
          <pdl-icon v-if="isCompactView" class="pointer-events-none" name="chevron_right" />
        </a>
        <!-- Single Item -->
        <a
          v-else-if="isNavSingleItem(navItem)"
          :href="composeLink(navItem.url.url)"
          :target="setTarget(navItem.url.target)"
          :qaid="navItem.url.uid"
          role="menuitem"
          class="px-3 py-2 nav-item__tier-1 nav__menu-item font-bold text-sm lg:text-xs flex items-center hover:underline"
          tabindex="-1"
          @keydown.prevent.up="
            (e) => {
              !isCompactView ? focusParentTier(e) : focusPreviousMenuItem(e);
            }
          "
          @keydown.prevent.right="
            (e) => {
              !isCompactView ? focusNextMenuItem(e) : focusNewTier(e);
            }
          "
          @keydown.prevent.left="
            (e) => {
              !isCompactView ? focusPreviousMenuItem(e) : focusParentTier(e);
            }
          "
        >
          {{ navItem.url.linkName }}
        </a>
        <nav-category-scroll
          v-if="hasNavScrollCategory(navItem)"
          :nav-item-data="navItem"
          :nav-back-button-label="navItem.url.linkName"
          :load-nav-images="loadNavImages"
        />
        <nav-content
          v-else
          :nav-item-data="navItem"
          :active-layout="activeLayout(navItem)"
          :nav-back-button-label="navItem.url.linkName"
          :classes="b2bClass"
          :is-b2b="isB2b"
          :load-nav-images="loadNavImages"
        />
      </li>
    </ul>
  </div>
  <nav-content
    v-else
    :nav-item-data="navItemData[0]"
    :active-layout="activeLayout(navItemData[0])"
    :nav-back-button-label="label"
    :classes="`nav__no-tabs bg-white ${b2bClass}`"
    :is-b2b="isB2b"
    :load-nav-images="loadNavImages"
  />
</template>

<script>
import NavCategoryScroll from '@/navigation/NavCategoryScroll.vue';
import NavContent from '@/navigation/NavContent.vue';
import NavBackButton from '@/navigation/NavBackButton.vue';
import {PdlIcon} from '@pedal/pdl-icon';
import {ProductCardType} from '@/navigation/utils/nav-constants.js';
import {mapState} from 'vuex';
import links from '@/navigation/mixins/links.js';

export default {
  name: 'TierOneNavItems',
  components: {
    NavCategoryScroll,
    NavContent,
    NavBackButton,
    PdlIcon,
  },
  mixins: [links],
  inject: ['focusNewTier', 'focusParentTier', 'focusNextMenuItem', 'focusPreviousMenuItem', 'isElectra', 'currentTier'],

  props: {
    navItemData: {
      type: Array,
      default: null,
    },
    label: {
      type: String,
      default: '',
    },
    isB2b: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    ...mapState('global', ['isCompactView']),
    hasTierOneSubNavItems() {
      return this.navItemData.length > 1;
    },
    filteredNavItems() {
      return this.navItemData.filter((item) => item.url && item.visible);
    },
    cardColumns() {
      return this.navItemData.categoryCardEditorialComponentData ? 'grid-cols-3 col-span-3' : 'grid-cols-4';
    },
    b2bClass() {
      return {'nav-b2b': this.isB2b};
    },
    listClasses() {
      return {
        'justify-center': !this.navItemData.marketingCopy,
        'flex-col': this.navItemData.marketingCopy,
      };
    },
    layoutClasses() {
      return {
        'flex flex-col': !this.navItemData.marketingCopy,
        'grid grid-cols-4': this.navItemData.marketingCopy,
      };
    },
    currentLink() {
      return this.navItemData.category ? `/c/${this.navItemData.category}` : this.navItemData.link;
    },
  },
  methods: {
    loadNavImages(container) {
      const images = container.querySelectorAll('img[data-nav-image]');
      if (!images.length) return;
      images.forEach((image) => {
        image.src = image.dataset.navImage;
        image.removeAttribute('data-nav-image');
      });
    },
    hasChildren(item) {
      return item.children?.length > 0;
    },
    isAfterMarketProduct(item) {
      return item.productCardType === ProductCardType.AFTERMARKET;
    },
    hasSecondaryLinks(item) {
      return item.secondaryLinks?.length > 0;
    },
    hasLinkAssetID(item) {
      return Boolean(item.linkAssetID);
    },
    hasCardLayout(item) {
      return (
        !this.hasChildren(item) &&
        item.links?.every((link) => item.productCardType === ProductCardType.BIKE && this.hasLinkAssetID(link))
      );
    },
    hasMixedCardLayout(item) {
      const noChildren = !this.hasChildren(item);
      const hasAfterMarketItem = item.links?.some(
        (link) => this.isAfterMarketProduct(item) && this.hasLinkAssetID(link)
      );
      const hasLinkWithNoImage = item.links.some((link) => !this.hasLinkAssetID(link));
      return noChildren && hasAfterMarketItem && hasLinkWithNoImage;
    },
    hasAccessoryCardLayout(item) {
      return (
        !this.hasChildren(item) &&
        item.links?.every((link) => this.isAfterMarketProduct(item) && this.hasLinkAssetID(link))
      );
    },
    hasB2bStyleLayout(item) {
      return this.hasChildren(item) && !this.hasSecondaryLinks(item);
    },
    hasNavScrollCategory(item) {
      const hasChildren = this.hasChildren(item);
      const hasSecondaryLinks = this.hasSecondaryLinks(item);
      const isintroTextMissing = !item.introText;
      return hasChildren && hasSecondaryLinks && isintroTextMissing;
    },
    activeLayout(item) {
      if (this.hasCardLayout(item)) return 'cardLayout';
      if (this.hasMixedCardLayout(item)) return 'mixedCardLayout';
      if (this.hasAccessoryCardLayout(item)) return 'accessoryCardLayout';
      if (this.hasB2bStyleLayout(item)) return 'b2bStyleLayout';
      return null;
    },
    navItemVisible(item) {
      return (
        item?.url?.hasVisibleProducts !== false &&
        item?.url?.visible !== false &&
        (this.hasChildren(item) || item.links?.length > 0)
      );
    },
    isNavSingleItem(item) {
      return !!(item?.url?.hasVisibleProducts !== false && item?.url?.visible !== false);
    },
  },
};
</script>

<style lang="scss">
.nav-item__tier-1 {
  &[aria-expanded='true'] {
    background: var(--nav-tier-1-bg-active);
    z-index: 40;
  }
}

.nav__tier-1 {
  --nav-tier-1-transition: opacity 0s ease-in-out var(--nav-reduced-motion, 0.2s);
  --nav-tier-1-height: 52px;

  color: var(--nav-tier-1-color);
  opacity: 0;
  transition: var(--nav-tier-1-transition);
  pointer-events: none;
  z-index: -1;

  &::after {
    content: '';
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: -1;
    transform-origin: top center;
    background: var(--nav-tier-1-bg);
  }

  @media (screen(lg)) {
    --tier-1-scale: 1 0;
    --nav-tier-1-transition: scale var(--nav-reduced-motion, 0.2s) ease, opacity 0s ease 0s;

    opacity: 1;
    transform-origin: top center;
    scale: var(--tier-1-scale);
    background: var(--nav-tier-1-bg);
  }

  [aria-expanded='true'] + & {
    --nav-tier-1-transition: opacity 0s ease-in-out;

    opacity: 1;
    pointer-events: all;
    z-index: 2;

    @media (screen(lg)) {
      --nav-tier-1-transition: scale var(--nav-reduced-motion, 0.2s) ease, opacity 0s ease-in-out;
      --tier-1-scale: 1 1;
    }
  }

  .active--tier-0 & {
    --nav-tier-1-transition: opacity 0s ease-in-out var(--nav-reduced-motion, 0.2s);

    @media (screen(lg)) {
      --nav-tier-1-transition: scale var(--nav-reduced-motion, 0.2s) ease, opacity 0s ease-in-out;
    }
  }

  .active--tier-1 & {
    @media (screen(lg)) {
      --tier-1-scale: 1 1;
      --nav-tier-1-transition: scale 0s ease var(--nav-reduced-motion, 0.2s),
        opacity var(--nav-reduced-motion, 0.2s) ease-in-out var(--nav-reduced-motion, 0.2s);

      opacity: 0;
      z-index: 1;

      &:has([aria-expanded='true']) {
        // prettier-ignore
        --nav-tier-1-transition: scale 0s ease var(--nav-reduced-motion, 0.2s), opacity var(--nav-reduced-motion, 0.2s) ease-in-out;

        opacity: 1;
        z-index: 2;
      }
    }
  }
}

.nav__content {
  --nav-content-bg: rgb(255 255 255);
  // prettier-ignore
  --nav-tier-2-transition: opacity var(--nav-reduced-motion, 0.2s) ease-in var(--nav-reduced-motion, 0.2s), translate 0s ease-out var(--nav-reduced-motion, 0.2s), visibility 0s ease var(--nav-reduced-motion, 0.2s);

  transition: var(--nav-tier-2-transition);
  background-color: var(--nav-content-bg);

  [aria-expanded='true'] + & {
    // prettier-ignore
    --nav-tier-2-transition: opacity var(--nav-reduced-motion, 0.2s) ease-in, translate var(--nav-reduced-motion, 0.2s) ease-out, visibility 0s ease;
  }

  @media (screen(lg)) {
    --nav-tier-2-clip: polygon(0 0, 100% 0, 100% 0, 0 0);
    // prettier-ignore
    --nav-tier-2-transition: clip-path var(--nav-reduced-motion, 0.2s) ease, visibility 0s ease var(--nav-reduced-motion, 0.2s);

    clip-path: var(--nav-tier-2-clip);
    max-height: calc(85vh - var(--nav-tier-1-height, 0px) - var(--header-height));
    pointer-events: none;
    z-index: 1;

    &.nav-b2b {
      max-height: calc(100dvh - var(--nav-tier-1-height, 0px) - var(--header-height));
    }

    [aria-expanded='true'] + & {
      --nav-tier-2-clip: polygon(0 0, 100% 0, 100% 100%, 0 100%);
      --nav-tier-2-transition: clip-path var(--nav-reduced-motion, 0.2s) ease, visibility 0s ease;

      z-index: 2;
      pointer-events: all;
    }

    .active--tier-1 & {
      --nav-tier-2-clip: polygon(0 0, 100% 0, 100% 100%, 0 100%);
      // prettier-ignore
      --nav-tier-2-transition: clip-path var(--nav-reduced-motion, 0.2s) ease, opacity var(--nav-reduced-motion, 0.4s) ease var(--nav-reduced-motion, 0.4s), visibility 0s ease var(--nav-reduced-motion, 0.8s);

      opacity: 0;
    }

    .active--tier-1 [aria-expanded='true'] + & {
      // prettier-ignore
      --nav-tier-2-transition: clip-path var(--nav-reduced-motion, 0.2s) ease, opacity var(--nav-reduced-motion, 0.4s) ease, visibility 0s ease;

      opacity: 1;
    }
  }
}
</style>
